import axios from "axios";
import router from "@/router";
import keycloak from "@/keycloak";

// Singleton do Axios.
// Todos os pedidos do ODC passam por esta instância.
const axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_DOMAIN,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
    },
});

const errorPages = {
    404: "/404",
    500: "/500",
};

/*
 * =========================================================
 * REQUEST INTERCEPTOR
 * =========================================================
 *
 * Antes de cada pedido:
 * - confirma que o Keycloak está autenticado;
 * - tenta renovar o token;
 * - envia o access token no Authorization header.
 */
axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            if (keycloak.authenticated) {
                /*
                 * Renova o token se faltar menos de 30 segundos
                 * para expirar.
                 */
                await keycloak.updateToken(30);

                if (keycloak.token) {
                    config.headers = config.headers || {};

                    config.headers.Authorization =
                        `Bearer ${keycloak.token}`;
                }
            }

            return config;
        } catch (error) {
            console.error(
                "Erro ao renovar token do Keycloak:",
                error
            );

            /*
             * Aqui o refresh do próprio Keycloak falhou.
             *
             * Neste caso faz sentido regressar ao login
             * central, porque já não conseguimos manter
             * uma sessão válida.
             */
            keycloak.login({
                redirectUri: window.location.href,
            });

            return Promise.reject(error);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);


/*
 * =========================================================
 * RESPONSE INTERCEPTOR
 * =========================================================
 */
axiosInstance.interceptors.response.use(
    (response) => response,

    async (error) => {
        const statusCode =
            error?.response?.status ?? null;

        /*
         * =====================================================
         * 401
         * =====================================================
         *
         * Um 401 do backend NÃO significa obrigatoriamente
         * que seja necessário voltar a fazer login.
         *
         * Primeiro:
         * - força refresh do token;
         * - repete o pedido uma única vez.
         *
         * Se o backend continuar a devolver 401,
         * deixamos o erro chegar à aplicação.
         *
         * Isto evita:
         *
         * 401
         * -> Keycloak login
         * -> volta ao ODC
         * -> 401
         * -> Keycloak login
         * -> loop infinito
         */
        if (statusCode === 401) {
            try {
                /*
                 * Força uma renovação do token,
                 * mesmo que o Keycloak considere
                 * que ele ainda não expirou.
                 */
                await keycloak.updateToken(-1);

                if (
                    keycloak.token &&
                    error.config
                ) {
                    const originalRequest =
                        error.config;

                    /*
                     * Só podemos repetir o pedido
                     * uma vez.
                     */
                    if (
                        !originalRequest.__keycloakRetry
                    ) {
                        originalRequest.__keycloakRetry =
                            true;

                        originalRequest.headers =
                            originalRequest.headers || {};

                        originalRequest.headers.Authorization =
                            `Bearer ${keycloak.token}`;

                        return axiosInstance(
                            originalRequest
                        );
                    }
                }
            } catch (refreshError) {
                console.error(
                    "Erro ao fazer refresh forçado do token Keycloak:",
                    refreshError
                );
            }

            /*
             * IMPORTANTE:
             *
             * Não fazemos keycloak.login() aqui.
             *
             * Se depois do refresh o pedido continua
             * a devolver 401, queremos ver qual é o
             * endpoint que está a falhar.
             */
            console.error(
                "Pedido continuou a devolver 401 após refresh do Keycloak:",
                error?.config?.url
            );

            return Promise.reject(error);
        }


        /*
         * =====================================================
         * PÁGINAS DE ERRO
         * =====================================================
         */
        const errorPage =
            errorPages[statusCode];

        if (errorPage) {
            const currentPath =
                router.currentRoute?.value?.path ??
                router.currentRoute?.path;

            if (
                currentPath !== errorPage
            ) {
                router
                    .push(errorPage)
                    .catch?.(() => {});
            }
        }

        return Promise.reject(error);
    }
);


export default class API
{
    static getApiUrl()
    {
        return process.env.VUE_APP_API_DOMAIN;
    }


    /*
     * =========================================================
     * START REQUEST
     * =========================================================
     *
     * Mantido por compatibilidade com código existente.
     *
     * Com Keycloak já não precisamos de pedir
     * sanctum/csrf-cookie.
     */
    static async startRequest()
    {
        if (
            keycloak.authenticated &&
            keycloak.token
        ) {
            try {
                await keycloak.updateToken(30);
            } catch (error) {
                console.error(
                    "Não foi possível atualizar o token Keycloak:",
                    error
                );

                /*
                 * Aqui o próprio refresh falhou.
                 * Portanto a sessão Keycloak pode realmente
                 * ter terminado.
                 */
                keycloak.login({
                    redirectUri:
                        window.location.href,
                });

                throw error;
            }
        }

        return true;
    }


    /*
     * =========================================================
     * AXIOS INSTANCE
     * =========================================================
     */
    static getAxiosInstance()
    {
        return axiosInstance;
    }


    /*
     * =========================================================
     * HTTP METHODS
     * =========================================================
     */
    static async get(url, data)
    {
        return axiosInstance.get(
            url,
            data
        );
    }


    static async post(url, data)
    {
        return axiosInstance.post(
            url,
            data
        );
    }


    static async put(url, data)
    {
        return axiosInstance.put(
            url,
            data
        );
    }


    static async patch(url, data)
    {
        return axiosInstance.patch(
            url,
            data
        );
    }


    static async delete(url, data)
    {
        return axiosInstance.delete(
            url,
            data
        );
    }
}